import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  invalidateMozartNotifications,
  logAnalyticMozartDefectAlert,
  reloadStateNotificationsNew,
  setNotificationSeenById,
  setTurnOffProduct,
} from 'features/mozart-notifications/infra';
import appActions from 'redux/actions/app-actions/app.actions';
import { useMozartTurnOffProductLang } from './useMozartTurnOffProductLang';

import type {
  MozartComplaint,
  TurnOffModeOption,
} from 'features/mozart-notifications/domain';
import type { ChangeEvent } from 'react';
import type { ModalProps } from '@material-ui/core/Modal';

const OPTION_INIT: TurnOffModeOption | undefined = undefined;

export function useMozartTurnOffProduct(props: Props) {
  const { complaint, onClose = () => {} } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(OPTION_INIT);

  const dispatchRedux = useDispatch();
  const lang = useMozartTurnOffProductLang();

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value as TurnOffModeOption);
  };

  const sendSeenAndAnalytic = () => {
    logAnalyticMozartDefectAlert({
      complaint,
      action: 'turn_off_product',
      turnOffTime: selectedOption,
    });
    setNotificationSeenById({
      id: complaint.id,
      createdAt: complaint.createdAt,
    }).then();
  };

  const _handleClose = () => {
    setTimeout(() => {
      setIsLoading(false);
      invalidateMozartNotifications();
      reloadStateNotificationsNew();
      dispatchRedux(
        appActions.openSnackBarSuccess(
          '',
          lang.snackBar.success.msg[selectedOption!],
          5_000,
        ),
      );
      onClose({}, 'escapeKeyDown');
    }, 1_000);
  };

  const handleOnClickContinue = () => {
    setIsLoading(true);
    setTurnOffProduct({
      storeId: complaint.storeId,
      productId: complaint.productId,
      deactivationOption: selectedOption!,
    })
      .then(() => {
        sendSeenAndAnalytic();
        _handleClose();
      })
      .catch(_ => {
        setIsLoading(false);
        dispatchRedux(
          appActions.openSnackBarError(
            lang.snackBar.error.title,
            lang.snackBar.error.msg,
            3_000,
          ),
        );
      });
  };

  return {
    lang,
    isLoading,
    isDisabled: !selectedOption || isLoading,
    handleOptionChange,
    handleOnClickContinue,
  };
}

interface Props {
  complaint: MozartComplaint;
  onClose: ModalProps['onClose'];
}
